import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-619bd912"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["data-unitValueField", "data-unitValueType", "data-unitValueTagId", "value", "title", "readonly"]

import { InformationCircleIcon, TrashIcon, EyeIcon, EyeSlashIcon } from "@heroicons/vue/24/solid";
import { onMounted, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import numberService from "@/services/numberService";
import {
  UnitValue,
  UnitValueCellNavigation,
  UnitValueCellNavigationDirection,
  UnitValueDetailContext,
} from "@/types/UnitValue";
import { createFormatNumber, getTagId, precision } from "@/views/unitValues/unitValues";


export default /*#__PURE__*/_defineComponent({
  __name: 'ValueCellNumberInput',
  props: {
  name: {
    type: String as PropType<keyof UnitValue>,
    required: true,
  },
  value: {
    type: Number as PropType<number | null>,
    required: false,
  },
  originalValue: {
    type: Number as PropType<number | null>,
    required: false,
  },
  hasError: {
    type: Boolean,
    required: false,
  },
  highlightWhenZero: {
    type: Boolean,
    required: false,
  },
  cls: {
    type: String,
    required: false,
  },
  showClear: {
    type: Boolean,
    required: false,
  },
  infoPosition: {
    type: String as PropType<"none" | "left" | "right">,
    required: true,
  },
  showUnIgnore: {
    type: Boolean,
    required: false,
  },
  showIgnore: {
    type: Boolean,
    required: false,
  },
  warningTriangle: {
    type: Boolean,
    required: false,
  },
  unitValue: {
    type: Object as PropType<UnitValue>,
    required: true,
  },
  rowHeight: {
    type: Number,
    required: true,
  },
  valueCellDetailContext: {
    type: Object as PropType<UnitValueDetailContext | null>,
    required: false,
  },
},
  emits: [
  "changed",
  "infoShown",
  "infoHidden",
  "unIgnoreClicked",
  "ignoreClicked",
  "focused",
  "blurred",
  "navigated",
],
  setup(__props, { emit: __emit }) {

const formatNumber = createFormatNumber(useI18n().locale.value);

const props = __props;

const emit = __emit;

const numberText = ref("");
const isEditMode = ref(false);
const originalValueSnapshot = ref<number | null>(null);

const formatNumberText = (value: number | null) =>
  formatNumber(value ?? null, { disableGrouping: true }) ?? "";

const updateNumberText = () => {
  numberText.value = formatNumberText(props.value ?? null);
};

const emitChanged = (value: number | null) => {
  emit("changed", {
    name: props.name,
    value,
  });
};

const setNumberTextAndEmitParsedValue = (text: string) => {
  numberText.value = text;
  const parsedValue = text ? numberService.parseText(text, precision) : null;
  emitChanged(parsedValue);
};

const setIsEditMode = (newIsEditMode: boolean) => {
  isEditMode.value = newIsEditMode;
  if (newIsEditMode) {
    originalValueSnapshot.value = props.originalValue ?? null;
  }
};

const confirmValue = () => {
  setIsEditMode(false);
  updateNumberText();
  // here there is no explicit emitChanged as in clearAndConfirmValue,
  // because the value is updated immediately after the key is pressed
  if (Number.isNaN(props.value)) {
    // clear invalid value, as the current validation library can't handle when multiple attributes are invalid
    emitChanged(null);
  }
};

const clearAndConfirmValue = () => {
  setIsEditMode(false);
  emitChanged(null);
};

const restoreAndConfirmValue = () => {
  setIsEditMode(false);
  emitChanged(originalValueSnapshot.value);
};

const isMatch = (text: string) => text.match(/^[\d.,]+$/);

const hideTooltip = () => {
  if (props.valueCellDetailContext) {
    emit("infoHidden");
  }
};

const toggleInfoTooltip = (event: Event) => {
  if (
    props.valueCellDetailContext &&
    props.valueCellDetailContext.unitValue._id === props.unitValue._id
  ) {
    hideTooltip();
  } else {
    const target = event.currentTarget as HTMLElement;
    const clientRect = target.getBoundingClientRect();
    const x = clientRect.left + clientRect.width;
    const y = clientRect.top - 4;
    emit("infoShown", [x, y]);
  }
};

const navigateCell = (direction: UnitValueCellNavigationDirection) => {
  const unitValueCellNavigation: UnitValueCellNavigation = {
    field: props.name,
    direction,
    fields: [],
  };
  emit("navigated", unitValueCellNavigation);
};

const handleBeforeInput = (event: Event) => {
  const inputEvent = event as InputEvent;
  if (inputEvent.data && !isMatch(inputEvent.data)) {
    event.preventDefault();
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (isEditMode.value) {
    if (event.key === "Enter") {
      // to prevent form from submitting
      event.preventDefault();
      confirmValue();
      navigateCell("down");
    }
    if (event.key === "Escape") {
      restoreAndConfirmValue();
      hideTooltip();
    }
  } else {
    if (isMatch(event.key)) {
      setIsEditMode(true);
      event.preventDefault();
      setNumberTextAndEmitParsedValue(event.key);
    }
    if (event.key === "Enter") {
      // to prevent form from submitting
      event.preventDefault();
      setIsEditMode(true);
    }
    if (event.key === "F2") {
      setIsEditMode(true);
    }
    if (event.key === "Delete") {
      clearAndConfirmValue();
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      // to prevent scrollbars to scroll
      event.preventDefault();
      navigateCell(event.key === "ArrowUp" ? "up" : "down");
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      // to prevent scrollbars to scroll
      event.preventDefault();
      navigateCell(event.key === "ArrowLeft" ? "left" : "right");
    }
    if (event.key === "Home" || event.key === "End") {
      // to prevent scrollbars to scroll
      event.preventDefault();
      navigateCell(event.key === "Home" ? "top" : "bottom");
    }
  }

  if (event.key === "i") {
    event.preventDefault();
    toggleInfoTooltip(event);
  }
};

const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  setNumberTextAndEmitParsedValue(value);
};

const handleBlur = () => {
  confirmValue();
  hideTooltip();
  emit("blurred");
};

const handleClearClick = () => {
  emitChanged(null);
};

const handleMouseDown = (event: MouseEvent) => {
  if (event.detail > 1) {
    event.preventDefault();
    setIsEditMode(true);
  }
};

const handleInfoIconMouseEnter = (event: Event) => {
  const target = event.currentTarget as HTMLElement;
  const clientRect = target.getBoundingClientRect();
  emit("infoShown", [clientRect.left, clientRect.top]);
};

onMounted(() => {
  updateNumberText();
});

watch(
  () => props.value,
  () => {
    if (!isEditMode.value) {
      updateNumberText();
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["relative h-full container", {
      'bg-red-50': __props.hasError,
      'bg-gray-100': !__props.hasError && __props.highlightWhenZero && __props.value === 0,
      [`- ${__props.cls}`]: !__props.hasError && !!__props.cls,
      'text-gray-300': __props.showUnIgnore,
    }])
  }, [
    _createElementVNode("input", {
      "data-unitValueField": __props.name,
      "data-unitValueType": __props.unitValue.type,
      "data-unitValueTagId": _unref(getTagId)(__props.unitValue.tags),
      type: "text",
      class: _normalizeClass(["border-2 border-transparent w-full text-md p-1 text-center outline-none valueInput bg-transparent cursor-text read-only:cursor-pointer", {
        valueInputWithInfo: __props.infoPosition !== 'none',
      }]),
      style: _normalizeStyle(`box-shadow: none; height: ${__props.rowHeight - 1}px`),
      value: numberText.value,
      onBeforeinput: handleBeforeInput,
      onInput: handleInput,
      onKeydown: handleKeyDown,
      title: numberText.value,
      onFocus: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('focused'))),
      onBlur: handleBlur,
      readonly: !isEditMode.value,
      onMousedown: handleMouseDown
    }, null, 46, _hoisted_1),
    (__props.showClear)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["absolute top-0 clearTriangle", { clearTriangleAsWarning: __props.warningTriangle }]),
          style: {"right":"-3px"}
        }, null, 2))
      : _createCommentVNode("", true),
    (__props.infoPosition !== 'none')
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "absolute bg-gray-600 items-center gap-2 hidden detailContainer px-1 top-0",
          style: _normalizeStyle(`z-index: 100000; height: ${__props.rowHeight - 1}px; ${
        __props.infoPosition === 'left'
          ? 'left: 0px; transform: translateX(-100%); direction: rtl'
          : 'right: 0px; transform: translateX(100%)'
      }`)
        }, [
          _createVNode(_unref(InformationCircleIcon), {
            class: "w-4 h-4 text-white cursor-pointer",
            onMouseenter: handleInfoIconMouseEnter,
            onMouseleave: hideTooltip
          }),
          (__props.showUnIgnore)
            ? (_openBlock(), _createBlock(_unref(EyeIcon), {
                key: 0,
                class: "w-4 h-4 text-white cursor-pointer",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('unIgnoreClicked')))
              }))
            : _createCommentVNode("", true),
          (__props.showIgnore)
            ? (_openBlock(), _createBlock(_unref(EyeSlashIcon), {
                key: 1,
                class: "w-4 h-4 text-white cursor-pointer",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('ignoreClicked')))
              }))
            : _createCommentVNode("", true),
          (__props.showClear)
            ? (_openBlock(), _createBlock(_unref(TrashIcon), {
                key: 2,
                class: "w-4 h-4 cursor-pointer text-white",
                onClick: handleClearClick
              }))
            : _createCommentVNode("", true)
        ], 4))
      : _createCommentVNode("", true)
  ], 2))
}
}

})